import React, { useState, useEffect, useRef } from 'react'
import PageHeader from '@components/PageHeader'
import './index.scss'

import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '@auth/AuthProvider'
import EventsController, { Event, ExternalEventDetails } from '@data/controller/EventsController'

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DateTime } from 'luxon'
import FadeIn from 'react-fade-in'

export default function CreateEventDetails() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMountedRef = useRef(true);

  // allow prefill from external import
  const externalPrefill = (location.state as any)?.externalEvent;

  // Get query params
  const queryTitle = searchParams.get('title');
  const queryDescription = searchParams.get('description');
  const queryRegistrationURL = searchParams.get('registrationURL');
  const queryThumbnailURL = searchParams.get('thumbnailURL');
  const queryExternalEventID = searchParams.get('externalEventID');
  const queryExternalProvider = searchParams.get('provider');

  const [title, setTitle] = useState(externalPrefill?.name || queryTitle || '');
  const [startsAt, setStartsAt] = useState<Date | null>(new Date());
  const [endsAt, setEndsAt] = useState<Date | null>(new Date());
  const [showTimes, setShowTimes] = useState(true);
  const [description, setDescription] = useState(queryDescription || '');
  const [locationText, setLocationText] = useState('');
  const [registrationURL, setRegistrationURL] = useState(queryRegistrationURL || '');
  const [thumbnailURL, setThumbnailURL] = useState(externalPrefill?.thumbnailImageURL || queryThumbnailURL || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalEventDetails, setExternalEventDetails] = useState<ExternalEventDetails | null>(null);
  const [loadingExternalDetails, setLoadingExternalDetails] = useState(false);
  const [roleMap, setRoleMap] = useState<Record<string, 'standard' | 'volunteer' | 'admin'>>({});

  // Fetch external event details if externalEventID and provider are present
  useEffect(() => {
    if (!queryExternalEventID || !queryExternalProvider || !auth.churchId) return;

    let isMounted = true;

    const fetchDetails = async () => {
      setLoadingExternalDetails(true);
      try {
        const resp = await EventsController.fetchExternalEventDetails(auth.churchId || '', queryExternalProvider, queryExternalEventID, auth);
        if (isMounted) {
          if (!resp.error && resp.body) {
            setExternalEventDetails(resp.body);
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch external event details:', err);
        }
      } finally {
        if (isMounted) {
          setLoadingExternalDetails(false);
        }
      }
    };

    fetchDetails();

    return () => {
      isMounted = false;
    };
  }, [queryExternalEventID, queryExternalProvider, auth.churchId, auth]);

  // Cleanup ref on unmount to prevent state updates after navigation
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onSubmit = async () => {
    if (!auth.churchId || !canSubmit()) {
      return;
    }

    setSaving(true);
    setError(null);

    const payload: Event = {
      id: '',
      churchID: auth.churchId,
      title,
      startsAt: startsAt ? startsAt.toISOString() : new Date().toISOString(),
      endsAt: endsAt ? endsAt.toISOString() : new Date().toISOString(),
      showTimes,
      description: description || null,
      location: locationText || null,
      registrationURL: registrationURL || null,
      thumbnailURL: thumbnailURL || null,
      waivers: []
    }

    let planningCenterMetadata = null;
    if (queryExternalProvider === 'PLANNING_CENTER') {
      planningCenterMetadata = {
        signupID: queryExternalEventID || '',
        selectionTypeMap: roleMap
      }
    }

    try {
      const resp = await EventsController.createEvent(auth.churchId, {
        event: payload,
        planningCenterMetadata
      }, auth);
      if (isMountedRef.current) {
        if (resp.error) {
          setError(resp.error);
        } else {
          navigate('/events');
        }
      }
    } catch (err: any) {
      if (isMountedRef.current) {
        setError(err?.message || 'Failed to create event');
      }
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  }

  const canSubmit = () => {
    if (saving) return false;
    if (!title || title.trim().length === 0) return false;
    if (!startsAt || !endsAt) return false;
    if (queryExternalProvider !== null && externalEventDetails === null) return false; // if we have a provider, we MUST wait for these to be loaded.

    // If using Planning Center, ensure all role mappings are set
    if (queryExternalProvider === 'PLANNING_CENTER') {
      const selectionTypes = externalEventDetails?.planningCenter?.selectionTypes || [];
      if (selectionTypes.length > 0) {
        const allRolesMapped = selectionTypes.every((type) => roleMap[type.id]);
        if (!allRolesMapped) return false;
      }
    }

    return true;
  }

  return <div className='create-event-internal'>
    <PageHeader title='' showBackButton={true} backLocation="/events/create" />

    <div className='form'>
      <h1>New Event</h1>

      {error ? <p className='error'>{error}</p> : null}

      <h3>Details</h3>
      <div className="section">
        <div className='input'>
          <input type="text" name='title' placeholder='Title' value={title} onChange={(e) => setTitle(e.target.value)} disabled={saving} />
        </div>

        <Separator />

        <div className='input'>
          <textarea name='description' placeholder='Event Description' value={description} onChange={(e) => setDescription(e.target.value)} disabled={saving} />
        </div>
      </div>

      <div className='section' style={{ marginTop: 18 }}>
        <div className='input'>
          <input type="text" name='registrationURL' placeholder='Registration Link' value={registrationURL} onChange={(e) => setRegistrationURL(e.target.value)} disabled={saving} />
        </div>

        <Separator />

        <div className='input'>
          <input type="text" name='location' placeholder='Location' value={locationText} onChange={(e) => setLocationText(e.target.value)} disabled={saving} />
        </div>
      </div>

      <h3>Schedule</h3>
      <div className="section">
        <div className="input">
          <label>Start Date</label>
          <DatePicker className='date-picker'
            selected={startsAt}
            disabled={saving}
            showTimeInput
            timeInputLabel='Time'
            timeFormat='p'
            dateFormat="Pp"
            onChange={(date) => {
              if (saving) return;

              setStartsAt(date);
            }} />
        </div>

        <Separator />

        <div className="input">
          <label>End Date</label>
          <DatePicker className='date-picker'
            selected={endsAt}
            disabled={saving}
            showTimeInput
            timeInputLabel='Time'
            timeFormat='p'
            dateFormat="Pp"
            onChange={(date) => {
              if (saving) return;

              setEndsAt(date);
            }} />
        </div>

        <Separator />

        <div className='input'>
          <label>Show times</label>
          <input type='checkbox' checked={showTimes} onChange={(e) => setShowTimes(e.target.checked)} disabled={saving} />
        </div>
      </div>
      <p className="footnote">Turning on "Show times" will display the time alongside the event dates in the Salt app.</p>

      {loadingExternalDetails ? <p className='footnote'>Loading external event details...</p> : null}

      {queryExternalProvider === 'PLANNING_CENTER' ?
        <PlanningCenterSpecificConfig
          details={externalEventDetails}
          roleMap={roleMap}
          setRoleMap={setRoleMap}
          saving={saving}
        />
        : null}

      <button disabled={!canSubmit()} className='submit' onClick={(e) => {
        e.preventDefault();
        onSubmit();
      }}>{saving ? "Saving..." : "Create Event"}</button>
    </div>
  </div>
}

function PlanningCenterSpecificConfig({
  details,
  roleMap,
  setRoleMap,
  saving
}: {
  details: ExternalEventDetails | null
  roleMap: Record<string, 'standard' | 'volunteer' | 'admin'>
  setRoleMap: (map: Record<string, 'standard' | 'volunteer' | 'admin'>) => void
  saving: boolean
}) {
  if (!details || !details.planningCenter?.selectionTypes || details.planningCenter.selectionTypes.length === 0) return null;

  return <FadeIn>
    <div className='planning-center-config'>
      <h3>Role Mapping</h3>

      <div className="section">
        {details.planningCenter.selectionTypes.map((selectionType, idx) => (
          <div key={selectionType.id}>
            {idx > 0 && <Separator />}
            <div className='input'>
              <label>{selectionType.name}</label>
              <select
                className='role-select'
                value={roleMap[selectionType.id] || ''}
                onChange={(e) => setRoleMap({ ...roleMap, [selectionType.id]: e.target.value as 'standard' | 'volunteer' | 'admin' })}
                disabled={saving}
              >
                <option value="">Select a role...</option>
                {(['standard', 'volunteer', 'admin'] as const).map((role) => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>

      <p className="footnote">Map each Planning Center selection type to a Salt role, so that if an attendee links their registration to their Salt account they will have an appropriate level of access.</p>
    </div>
  </FadeIn>
}

function Separator() {
  return <div style={{
    width: "100%",
    height: 1,
    backgroundColor: "#eaeaea",
    opacity: 0.10,
    margin: '12px 0'
  }} />
}